import { FC, useEffect } from 'react'
import Image from 'next/image'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { ListProductFilter } from '@/components/common/list'
import { CategoryNavigation } from '@/components/common/navigation'
import { SkeletonRootCategoryTab } from '@/components/common/navigation/skeleton'
import ListProducts from '@/components/domains/brand/ListProducts'
import { TopNavigation } from '@/components/domains/layouts/header'
import SeoHeader from '@/components/domains/layouts/header/SeoHeader'
import { S3_HOST_URL } from '@/constants/legacy/constEnv'
import ROUTES, { RouteKeyName } from '@/constants/legacy/constRoutes'
import MUTATION_VARIABLES from '@/containers/gqls/base/mutation_variables'
import { useApiMutation, useCustomRouter } from '@/containers/hooks'
import useBrandProductsListV2ContainerHook from '@/containers/hooks/brand/brandProductsListV2.container.hook'
import { UserContainer } from '@/containers/users/UserContainer'
import { AdPetType } from '@/graphql/generated/schemas'
import { doSetPetType } from '@/stores/reduxData'
import { getPetTypeRootCategoryKoreanText } from '@/utils/utilCategory'
import { localPetType } from '@/utils/utilLocalStorage'

const PageBrandDetail: FC = () => {
  const { query } = useCustomRouter()

  const dispatch = useDispatch()
  const { me } = UserContainer.useContainer()
  const [updatePetTypeMutation] = useApiMutation('updateDisplayPetType')

  const { brandId, petType, rootCategoryId } = query as {
    brandId: string
    petType: AdPetType
    rootCategoryId: string | undefined
  }

  const { isCategoryLoading, categoryData, brandProducts, orderBy, brandInfo } = useBrandProductsListV2ContainerHook()

  useEffect(() => {
    if (petType) {
      const _petType = petType.toUpperCase() as AdPetType
      dispatch(doSetPetType(_petType))
      localPetType.save(_petType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (petType && !!me?.id) {
      const params = {
        id: me.id,
        displayPetType: petType,
      }
      const variables = MUTATION_VARIABLES.UPDATE_DISPLAY_PET_TYPE(params)
      // @ts-ignore
      updatePetTypeMutation({ variables })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [query])

  const totalCount = brandProducts?.pages[0]?.page?.totalCount || 0

  return (
    <>
      <SeoHeader
        title={`반려동물을 위한 추천 ${brandInfo?.brand?.name || ''} - 핏펫몰}`}
        metaDescription={`핏펫이 추천하는 ${getPetTypeRootCategoryKoreanText(petType || AdPetType.Cat)} ${
          brandInfo?.brand?.name || ''
        }의 다양한 제품을 한자리에 모아봤습니다. 우리 ${getPetTypeRootCategoryKoreanText(
          petType || AdPetType.Cat
        )}에게 필요한 ${brandInfo?.brand?.name || ''}의 모든 제품을 한 번에 확인해보세요`}
        pathname={ROUTES.BRAND_DETAIL.replace(RouteKeyName.BrandId, brandId)}
      />
      <TopNavigation title={brandInfo?.brand?.name ?? ''} />
      {brandInfo?.brand?.backgroundImage && !rootCategoryId && (
        <StyledBackgroundImageContainer fixedHeight={220}>
          <Image
            alt={'brand-background-image'}
            src={`${S3_HOST_URL}/${brandInfo.brand.backgroundImage}`}
            layout="fill"
            objectFit="contain"
          />
        </StyledBackgroundImageContainer>
      )}
      {isCategoryLoading || !categoryData ? <SkeletonRootCategoryTab /> : <CategoryNavigation brandInfo={brandInfo} />}
      <StyledContainer id="brand-detail-list">
        {brandProducts && brandId && (
          <>
            <ListProductFilter orderBy={orderBy} totalCount={totalCount} />
            <ListProducts />
          </>
        )}
      </StyledContainer>
    </>
  )
}

const StyledContainer = styled.div`
  padding: ${({ theme: { spacing } }) => spacing.size24} ${({ theme: { spacing } }) => spacing.size16};
`

const StyledBackgroundImageContainer = styled.div<{ fixedHeight: number }>`
  position: relative;
  width: 100%;
  height: ${(props) => `calc(${props.fixedHeight} * min(100vw, 576px) / 375)`};
  background-color: ${(props) => props.theme.color.gray70};
`

export default PageBrandDetail
